import React, { useEffect, useRef, useState } from "react";

import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { twJoin } from "tailwind-merge";

import {
  CellDisplayType,
  getAvailableTypeOverrides,
  getCellTypeIcon,
} from "src/cases/caseView/blocks/cellTypes";
import { TableCurrencyCode } from "src/clients/case-management";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useOnClickOutside } from "src/hooks/usehooks-ts";

const CURRENCY_OPTIONS: {
  code: TableCurrencyCode;
  symbol: string;
}[] = [
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "BRL", symbol: "R$" },
  { code: "MXN", symbol: "MX$" },
  { code: "CAD", symbol: "CA$" },
  { code: "AUD", symbol: "AU$" },
];

const MenuRow: React.FC<{
  isActive: boolean;
  dataLoc: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ isActive, dataLoc, onClick, children }) => (
  <MenuItem>
    <button
      className={twJoin(
        "flex w-full items-center justify-between px-3 py-1.5",
        "font-inter-normal-12px text-gray-800",
        isActive ? "bg-gray-50" : "hover:bg-gray-50",
      )}
      data-loc={dataLoc}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">{children}</div>
      {isActive && <Icon icon={faCheck} size="2xs" variant="active" />}
    </button>
  </MenuItem>
);

/** Currency symbols, indented under the currency type so they read as its
 *  sub-selection. */
const CurrencyOptions: React.FC<{
  activeSymbol: TableCurrencyCode;
  onSelect: (symbol: TableCurrencyCode) => void;
}> = ({ activeSymbol, onSelect }) => (
  <div className="ml-5 flex" data-loc="cell-type-currency-options">
    <span className="my-1 w-0.5 shrink-0 bg-gray-200" />
    <div className="min-w-0 flex-1">
      {CURRENCY_OPTIONS.map(({ code, symbol }) => (
        <MenuRow
          key={code}
          dataLoc={`cell-type-currency-${code}`}
          isActive={code === activeSymbol}
          onClick={() => onSelect(code)}
        >
          <span className="w-6 text-center font-inter-normal-11px text-gray-400">
            {symbol}
          </span>
          <span>{code}</span>
        </MenuRow>
      ))}
    </div>
  </div>
);

type CellTypeSelectorProps = {
  currentType: CellDisplayType;
  declaredType: CellDisplayType;
  onTypeChange: (newType: CellDisplayType) => void;
};

export const CellTypeSelector: React.FC<CellTypeSelectorProps> = ({
  currentType,
  declaredType,
  onTypeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);

  useOnClickOutside([menuRef, itemsRef], () => setIsOpen(false));

  // onClickCapture bypasses MenuButton's internal state, so its
  // aria-expanded stays stale. Sync it via the data-loc attribute.
  useEffect(() => {
    const el = menuRef.current?.querySelector(
      '[data-loc="cell-type-selector-trigger"]',
    );
    el?.setAttribute("aria-expanded", String(isOpen));
  }, [isOpen]);

  const availableTypes = getAvailableTypeOverrides(
    currentType.type,
    declaredType.type,
  );
  // Include the current type at the top of the list so it appears selected
  const allTypes = [currentType.type, ...availableTypes];

  // Non-null exactly when the cell is a currency, which gates the symbol list.
  const activeSymbol =
    currentType.type === "currency" ? currentType.symbol : null;

  const handleSelect = (type: CellDisplayType["type"]) => {
    if (type === currentType.type) {
      setIsOpen(false);
      return;
    }
    if (type === "currency") {
      // Stay open so the symbol list below is reachable right away.
      onTypeChange({
        type: "currency",
        symbol: declaredType.type === "currency" ? declaredType.symbol : "USD",
      });
      return;
    }
    setIsOpen(false);
    onTypeChange({ type });
  };

  const handleCurrencySymbolSelect = (symbol: TableCurrencyCode) => {
    setIsOpen(false);
    onTypeChange({ type: "currency", symbol });
  };

  return (
    <Menu ref={menuRef} as="div">
      {/* Label-only: quick to appear, and closes with the pointer so it never
          hangs over the neighbouring cells. */}
      <Tooltip
        delay={10}
        isInteractive={false}
        placement="top"
        title="Change input type"
      >
        {/* onClickCapture prevents MenuButton's internal focus-management
            from blurring the active cell editor and deselecting the cell.
            We manage open state externally so the dropdown can stay open
            when picking a currency symbol. */}
        <div
          onClickCapture={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
        >
          <MenuButton
            as="div"
            className={twJoin(
              "flex h-5 w-5 cursor-pointer items-center justify-center rounded",
              "border border-gray-200 bg-white shadow-sm",
              "hover:bg-gray-50",
            )}
            data-loc="cell-type-selector-trigger"
          >
            <Icon icon={getCellTypeIcon(currentType.type)} size="3xs" />
          </MenuButton>
        </div>
      </Tooltip>

      {isOpen && (
        <MenuItems
          ref={itemsRef}
          anchor={{ to: "bottom end", gap: 4, padding: 8 }}
          className="z-[100] w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-xl"
          static
          onPointerDown={(e: React.PointerEvent) => e.preventDefault()}
        >
          {allTypes.map((type) => (
            <React.Fragment key={type}>
              <MenuRow
                dataLoc={`cell-type-option-${type}`}
                isActive={type === currentType.type}
                onClick={() => handleSelect(type)}
              >
                <Icon icon={getCellTypeIcon(type)} size="3xs" />
                <span>{type}</span>
              </MenuRow>
              {type === "currency" && activeSymbol && (
                <CurrencyOptions
                  activeSymbol={activeSymbol}
                  onSelect={handleCurrencySymbolSelect}
                />
              )}
            </React.Fragment>
          ))}
        </MenuItems>
      )}
    </Menu>
  );
};
